import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect } from "react";
import { useSelector } from "react-redux";

import { Button, Heading, SpacingToken, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { config } from "config/config";
import { LandingPages } from "constants/landing-pages";
import { ResponsiveBreakpoint } from "constants/responsive-breakpoint";
import { SettingsSection } from "constants/settings/sections";
import { EventPlace, trackEngagement } from "helpers/analytics";
import useSettingsSection from "hooks/settings/use-settings-section";
import { useOrganization } from "hooks/use-organizations";
import { canManageApplications } from "store/entities/applications";
import { selectHasLiveChatIntegration } from "store/entities/integrations";
import { withVar } from "themes/helpers";

import SettingsViewContainer from "../ViewContainer";
import { LivechatSettings } from "../integrations/pages/Livechat/LivechatSettings";

import { ChannelManagementAlert } from "./ChannelManagementAlert";
import liveChatChannelImg from "./assets/livechat-channel-img.png";

const signUpUTMs = "?utm_source=app.helpdesk.com&utm_medium=referral&utm_campaign=settings-channel";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const LiveChatChannel = () => {
  const { organization, isLoading } = useOrganization();

  const isLiveChatIntegrationEnabled = useSelector(selectHasLiveChatIntegration);
  const canManageApps = useSelector(canManageApplications);

  const hasLiveChat = organization?.products?.some(product => product.product === "LiveChat");

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleTryLcIntegrationClick = () => {
    trackEngagement("Settings: LiveChat section Main CTA clicked", EventPlace.Settings, {
      hasLiveChat,
    });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleLearnMoreClick = () => {
    trackEngagement("Settings: LiveChat section Learn more clicked", EventPlace.Settings, {
      hasLiveChat,
    });
  };

  useSettingsSection(SettingsSection.LiveChat);

  useEffect(() => {
    trackEngagement("Settings: LiveChat section opened", EventPlace.Settings, {
      integrationEnabled: isLiveChatIntegrationEnabled,
      hasLiveChat,
    });
  }, []);

  return (
    <SettingsViewContainer
      title="LiveChat"
      contentCss={css`
        overflow: "auto";
      `}
    >
      {isLiveChatIntegrationEnabled ? (
        canManageApps ? (
          <LivechatSettings />
        ) : (
          <Flex justifyContent="center">
            <Content>
              <ChannelManagementAlert />
            </Content>
          </Flex>
        )
      ) : (
        <Flex justifyContent="center">
          <Content>
            <Heading>Turn real-time chats into in-your-time tickets</Heading>
            <StyledText>
              Connect your HelpDesk with LiveChat and turn your live chats into manageable tickets. Handle cases on your
              terms, ensuring every customer receives a personalized, timely response.
            </StyledText>
            <Flex gap={withVar(SpacingToken.Spacing6)} marginBottom={withVar(SpacingToken.Spacing4)}>
              <Button
                kind="primary"
                disabled={!canManageApps}
                loading={isLoading}
                href={`${config.livechatAgentAppHost}/apps/marketplace/apps/helpdesk${hasLiveChat ? "" : signUpUTMs}`}
                target="_blank"
                onClick={handleTryLcIntegrationClick}
              >
                {hasLiveChat ? "Connect LiveChat" : "Try LiveChat for free"}
              </Button>
              <Button kind="link" href={LandingPages.LiveChat} target="_blank" onClick={handleLearnMoreClick}>
                Learn more
              </Button>
            </Flex>
            {!canManageApps && <ChannelManagementAlert />}
            <Image src={liveChatChannelImg} alt="LiveChat integration channel image" />
          </Content>
        </Flex>
      )}
    </SettingsViewContainer>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${withVar(SpacingToken.Spacing4)};
  align-items: center;

  text-align: center;
  max-width: 800px;
  padding: ${withVar(SpacingToken.Spacing8)} ${withVar(SpacingToken.Spacing16)} 0;
  margin-bottom: ${withVar(SpacingToken.Spacing8)};

  @media (max-width: ${ResponsiveBreakpoint.Tablet}px) {
    padding: ${withVar(SpacingToken.Spacing8)} ${withVar(SpacingToken.Spacing6)} 0;
  }

  @media (max-width: ${ResponsiveBreakpoint.Mobile}px) {
    padding: ${withVar(SpacingToken.Spacing8)} ${withVar(SpacingToken.Spacing2)} 0;
  }
`;

const StyledText = styled(Text)`
  max-width: 560px;
`;

const Image = styled.img`
  width: 100%;
  border-radius: ${withVar(SpacingToken.Spacing4)};
`;
